import { action, observable, computed } from 'mobx';
import { message } from 'antd';
import axios, { CancelTokenSource } from 'axios';
import { TestProjectRunResults, TestRunComparisonResult } from '../types';
import { TestProjectDashboardStore } from '.';
import { TestProjectService } from '../services';
import { Utils } from '../../common/services/Utils';
import { RouterStore } from '../../common/stores';
import { TestProjectsNavigation } from '../routes';

type ExecuteComparisonOptions = {
    skipNavigation?: boolean;
};

export default class TestProjectRunCompareStore {
    @observable
    comparisonResult: TestRunComparisonResult | null = null;

    @observable
    isLoading: boolean = false;

    @observable
    isSelectionMode: boolean = false;

    @observable
    selectedRunIds: Set<string> = new Set();

    private cancelTokenSource: CancelTokenSource | null = null;

    constructor(
        private readonly dashboardStore: TestProjectDashboardStore,
        private readonly routerStore: RouterStore,
        private readonly service: TestProjectService
    ) {}

    @computed
    get project() {
        return this.dashboardStore.currentProject;
    }

    @computed
    get testProjectId() {
        return this.dashboardStore.currentTestProjectId;
    }

    @action.bound
    setComparisonResult(comparisonResult: TestRunComparisonResult | null) {
        this.comparisonResult = comparisonResult;
    }

    @action.bound
    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    @action.bound
    setIsSelectionMode(isSelectionMode: boolean) {
        this.isSelectionMode = isSelectionMode;
    }

    @action.bound
    cancelSelectionMode() {
        this.setIsSelectionMode(false);
        this.selectedRunIds.clear();
    }

    @action.bound
    toggleRunSelection(runId: string) {
        if (this.selectedRunIds.has(runId)) {
            this.selectedRunIds.delete(runId);
            return;
        }

        if (this.selectedRunIds.size >= 2) {
            message.warning('You can only select up to 2 test runs for comparison');
            return;
        }

        this.selectedRunIds.add(runId);

        if (this.selectedRunIds.size === 2) {
            this.executeComparisonWithSelectedRuns();
        }
    }

    @action.bound
    compareWithSelectedRun(run: TestProjectRunResults) {
        this.setIsSelectionMode(true);
        this.selectedRunIds.clear();
        this.selectedRunIds.add(run.id);
    }

    @action.bound
    async compareWithPreviousRun(run: TestProjectRunResults) {
        const previousRun = this.getPreviousRun(run);

        if (!previousRun) {
            message.error('No previous run found');
            return;
        }

        try {
            this.selectedRunIds.add(run.id);
            this.selectedRunIds.add(previousRun.id);
            await this.executeComparison(run.id, previousRun.id);
        } finally {
            this.selectedRunIds.clear();
        }
    }

    @action.bound
    async matchCurrentRunCompareData(params: Record<string, string | undefined>) {
        this.dashboardStore.matchCurrentTestProjectData(params);

        const firstRunId = params.firstRunId;
        const secondRunId = params.secondRunId;

        if (!firstRunId || !secondRunId) {
            return;
        }

        if (!this.dashboardStore.testRuns.length) {
            await this.dashboardStore.loadTestRuns();
        }

        await this.executeComparison(firstRunId, secondRunId, { skipNavigation: true });
    }

    @action.bound
    private async executeComparisonWithSelectedRuns() {
        if (this.selectedRunIds.size !== 2) {
            return;
        }

        const [firstRunId, secondRunId] = Array.from(this.selectedRunIds);

        try {
            this.setIsSelectionMode(false);
            await this.executeComparison(firstRunId, secondRunId);
        } finally {
            this.selectedRunIds.clear();
        }
    }

    @action.bound
    private async executeComparison(
        firstRunId: string,
        secondRunId: string,
        options?: ExecuteComparisonOptions
    ): Promise<boolean> {
        if (!this.project?.id || !this.testProjectId) {
            return false;
        }

        this.cancelRequest();
        const currentCancelTokenSource = axios.CancelToken.source();
        this.cancelTokenSource = currentCancelTokenSource;

        try {
            this.setIsLoading(true);

            const response = await this.service.compareTestProjectRuns(
                this.project.id,
                this.testProjectId,
                firstRunId,
                secondRunId,
                currentCancelTokenSource.token
            );

            if (!response.isOk()) {
                if (this.cancelTokenSource !== currentCancelTokenSource) {
                    return false;
                }

                message.error(response.error?.data?.title || 'Failed to compare test runs');
                return false;
            }

            response.map(result => this.setComparisonResult(result));

            if (!this.comparisonResult?.changedTopicsByPackage.length) {
                message.info('No differences found between the two test runs');
                return true;
            }

            if (!options?.skipNavigation) {
                this.navigateToComparePage(firstRunId, secondRunId);
            }

            return true;
        } catch (err) {
            console.error(err);
            message.error('An error occurred while comparing test runs');
            return false;
        } finally {
            if (this.cancelTokenSource === currentCancelTokenSource) {
                this.setIsLoading(false);
                this.cancelTokenSource = null;
            }
        }
    }

    getPreviousRun(run: TestProjectRunResults): TestProjectRunResults | undefined {
        const previousRuns = this.dashboardStore.testRuns
            .filter(testRun => Utils.safeDateStringCompare(testRun.runTime, run.runTime) < 0)
            .sort((a, b) => Utils.safeDateStringCompare(b.runTime, a.runTime));

        return previousRuns[0];
    }

    goToDashboard() {
        this.dashboardStore.goToDashboard();
    }

    cancelRequest() {
        if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel();
            this.cancelTokenSource = null;
        }

        this.setIsLoading(false);
    }

    abort() {
        this.cancelRequest();
        this.cancelSelectionMode();
    }

    private navigateToComparePage(firstRunId: string, secondRunId: string) {
        if (!this.project?.id || !this.testProjectId) {
            return;
        }

        const path = TestProjectsNavigation.TestProjectRunComparePage.replace(':projectId', this.project.id)
            .replace(':testProjectId', this.testProjectId)
            .replace(':firstRunId', firstRunId)
            .replace(':secondRunId', secondRunId);

        this.routerStore.push(path);
    }
}
